import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Reusable "type-to-confirm" destructive-action dialog.
 * The confirm button stays disabled until the user types the expected
 * string EXACTLY (case-sensitive by default).
 */
export default function ConfirmDeleteDialog({
  open, onOpenChange,
  title, description,
  expectedInput, inputPlaceholder,
  onConfirm,
  confirmLabel,
  testIdPrefix = 'confirm-delete',
}) {
  const { t } = useTranslation();
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);

  const canConfirm = value === expectedInput && !busy;

  const doConfirm = async () => {
    setBusy(true);
    try {
      await onConfirm();
      onOpenChange(false);
      setValue('');
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || e?.message || 'Delete failed');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) setValue(''); }}>
      <DialogContent className="sm:max-w-md" data-testid={`${testIdPrefix}-dialog`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" /> {title}
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="py-2">
          <Label className="text-xs">{inputPlaceholder}</Label>
          <Input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={expectedInput}
            data-testid={`${testIdPrefix}-input`}
            autoFocus
          />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy} data-testid={`${testIdPrefix}-cancel`}>
            {t('common.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button
            variant="destructive"
            disabled={!canConfirm}
            onClick={doConfirm}
            data-testid={`${testIdPrefix}-confirm`}
          >
            {busy ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" />{t('common.deleting', { defaultValue: 'Deleting…' })}</> : (confirmLabel || t('common.delete', { defaultValue: 'Delete' }))}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
